import {
  ProjectStep,
  depositedByArchitect,
  type ProjectStepType,
} from '@autoriz/shared/project'

/**
 * Per #321 phase 2 (data-contracts.md C-14 / data-model.md):
 * derive the 7-position salesman stepper from the real 13-value
 * `ProjectStep` enum. The grouping mirrors the design-system mock
 * (`frontend/src/components/design-system/data.ts::STEPS`) but is
 * adapted to the real lifecycle: each `ProjectStep` value belongs
 * to exactly one of the 7 stepper positions.
 */

export type StepStatus = 'done' | 'current' | 'todo'

export type StepperStep = {
  num: string
  title: string
  sub: string
  /** One-sentence, client-facing explanation surfaced in the stepper popover. */
  explanation: string
  status: StepStatus
}

type StepDefinition = {
  key: string
  num: string
  title: string
  sub: string
  explanation: string
  matches: ReadonlyArray<ProjectStepType>
}

export const STEPS: ReadonlyArray<StepDefinition> = [
  {
    key: 'request',
    num: '01',
    title: 'Demande',
    sub: 'Création / soumission',
    explanation:
      "Vous renseignez les informations du projet, puis vous l'envoyez à l'architecte.",
    matches: [ProjectStep.PROJECT_INITIALIZED],
  },
  {
    key: 'verification',
    num: '02',
    title: 'Vérification',
    sub: 'Architecte vérifie',
    explanation:
      "L'architecte examine la demande et vérifie que le dossier est complet ; s'il la refuse, vous corrigez puis renvoyez.",
    // PROJECT_REFUSED is part of the verification loop, not a terminal state:
    // REQUESTED → (ACCEPTED | REFUSED → corrige/renvoie → REQUESTED). It stays
    // on this position so a refused project reads as "back at verification",
    // never "Fin". Mirrors PROJECT_STEP_ORDER (REFUSED ranks just after
    // REQUESTED) and the REQUEST action scope ([INITIALIZED, REFUSED]).
    matches: [ProjectStep.PROJECT_REQUESTED, ProjectStep.PROJECT_REFUSED],
  },
  {
    key: 'validation',
    num: '03',
    title: 'Validation',
    sub: 'DP ou PC selon urbanisme',
    explanation:
      "Le type d'autorisation (déclaration préalable ou permis de construire) est arrêté d'après l'analyse d'urbanisme.",
    matches: [
      ProjectStep.PROJECT_ACCEPTED,
      ProjectStep.PROJECT_ORDERED,
      ProjectStep.PROJECT_PAIED,
    ],
  },
  {
    key: 'drafting',
    num: '04',
    title: 'Rédaction',
    sub: 'Architecte rédige',
    explanation:
      "L'architecte rédige et prépare l'ensemble des documents du dossier.",
    matches: [
      ProjectStep.PROJECT_IN_PROGRESS,
      ProjectStep.PROJECT_FILE_COMPLETED,
      ProjectStep.PROJECT_FILE_REFUSED,
    ],
  },
  {
    key: 'sending',
    num: '05',
    title: 'Envoi',
    sub: 'À signer / déposer',
    explanation:
      'Les documents vous sont transmis pour signature, puis déposés en mairie.',
    matches: [ProjectStep.PROJECT_FILE_SENT],
  },
  {
    key: 'townhall',
    num: '06',
    title: 'Mairie',
    sub: 'Instruction',
    explanation:
      'La mairie instruit le dossier et rend sa décision sur la demande.',
    matches: [ProjectStep.PROJECT_FILE_SUBMITTED],
  },
  {
    key: 'end',
    num: '07',
    title: 'Fin',
    sub: 'Clôture du projet',
    explanation:
      'Le projet est clôturé une fois la décision de la mairie rendue.',
    matches: [ProjectStep.PROJECT_COMPLETED, ProjectStep.PROJECT_ABANDONED],
  },
] as const

const findStepIndex = (step: ProjectStepType): number => {
  for (let i = 0; i < STEPS.length; i += 1) {
    if (STEPS[i].matches.includes(step)) return i
  }
  return 0
}

/**
 * The 7-position lifecycle coordinates of a project's current step — used to
 * surface "Étape N/7 · Titre" on the salesman projects list cards (#377).
 */
export const stepPositionForProject = (
  step: ProjectStepType
): { position: number; total: number; title: string } => {
  const idx = findStepIndex(step)
  return { position: idx + 1, total: STEPS.length, title: STEPS[idx].title }
}

export const stepperStepsFromProject = (project: {
  step: ProjectStepType
  /**
   * `projects.deposit_mode`. Only the "Envoi" phase reads it — see
   * `sendingStepExplanation`. Optional, and an unset mode keeps the wording the
   * stepper has always used.
   */
  depositMode?: string | null
}): StepperStep[] => {
  const currentIdx = findStepIndex(project.step)
  return STEPS.map((s, idx) => ({
    num: s.num,
    title: s.title,
    sub: s.sub,
    explanation:
      s.key === 'sending'
        ? sendingStepExplanation(project.depositMode)
        : s.explanation,
    status: idx < currentIdx ? 'done' : idx === currentIdx ? 'current' : 'todo',
  }))
}

export type ActionTone =
  | 'primary'
  | 'wait'
  | 'amber'
  | 'blue'
  | 'success'
  | 'soft'
  | 'danger'

export type ActionBannerCopy = {
  tone: ActionTone
  eyebrow: string
  title: string
  // May contain `{remainingItems}` (substituted plural-aware by the salesman
  // hub's ActionBanner — see #326) and/or `{leadTime}` (substituted with the
  // architect lead-time literal). Tokens not in this short whitelist render
  // as literal text.
  text: string
  ctaLabel: string
  // Navigation target (relative route). The salesman hub's
  // `handlePrimaryClick` (#326) follows this when `ctaAction` is unset.
  ctaTo?: string
  // Backend project_action name (e.g., 'request'). When set, the salesman
  // hub fires `projectAction(GQL_CLIENT, projectID, ctaAction)` instead
  // of navigating. Used for the salesman's state-transition CTAs:
  // PROJECT_INITIALIZED → 'request' (send dossier to architect),
  // PROJECT_REFUSED → 'request' (resubmit after client rework).
  ctaAction?: string
  // When true, the salesman hub (#326) disables the primary CTA while the
  // page-level send-gate count is non-zero. Defaults to false (CTA always
  // enabled, orchestrator decides the click target).
  gated?: boolean
}

const ACTION_BANNERS = {
  [ProjectStep.PROJECT_INITIALIZED]: {
    tone: 'amber',
    // #668 — "À VOTRE TOUR" shouted above "l'étude urbaine est en cours
    // d'instruction". The chip is quieter now, and `bannerState` replaces it
    // outright when the dossier is genuinely waiting on somebody else.
    eyebrow: 'À vous de jouer',
    title: "Complétez le dossier, puis envoyez-le à l'architecte",
    // `{remainingItems}` names the owed pieces when they are all that is left —
    // see `formatGateShortfall`.
    text: 'Il manque {remainingItems}.',
    ctaLabel: 'Envoyer la demande',
    ctaAction: 'request',
    gated: true,
  },
  [ProjectStep.PROJECT_REQUESTED]: {
    tone: 'wait',
    eyebrow: 'En cours',
    title: "L'architecte vérifie votre projet",
    text: "Vous serez notifié dès qu'un retour sera disponible.",
    // No CTA — the salesman has nothing actionable while the architect
    // is verifying. The ActionBanner suppresses the button when
    // ctaLabel is empty (see `hasCta` in ActionBanner.tsx).
    ctaLabel: '',
  },
  [ProjectStep.PROJECT_ACCEPTED]: {
    // Issue #364: étape 3 is now "Validation du type" (DP/PC selon
    // urbanisme), not devis/paiement. A DP project assigned to a company
    // is auto-validated by the backend and never lands here; a PC project
    // still needs the salesman or architect to validate the recommended
    // type, exactly as before.
    tone: 'amber',
    eyebrow: 'À vous de jouer',
    title: 'Validez le type de projet recommandé',
    text: "Confirmez le type d'autorisation (DP ou PC) retenu d'après l'analyse d'urbanisme pour démarrer la rédaction du dossier.",
    ctaLabel: 'Valider le type',
  },
  [ProjectStep.PROJECT_ORDERED]: {
    tone: 'amber',
    eyebrow: 'À vous de jouer',
    title: 'Devis à régler',
    text: 'Réglez le devis pour démarrer la rédaction des documents.',
    ctaLabel: 'Régler le devis',
  },
  [ProjectStep.PROJECT_PAIED]: {
    tone: 'success',
    eyebrow: 'En cours',
    title: 'Paiement reçu',
    text: "L'architecte commence la rédaction des documents.",
    // No CTA — once the payment is received the salesman has nothing
    // actionable while the architect drafts the documents. The ActionBanner
    // suppresses the button when ctaLabel is empty (see `hasCta` in
    // ActionBanner.tsx).
    ctaLabel: '',
  },
  [ProjectStep.PROJECT_IN_PROGRESS]: {
    tone: 'blue',
    eyebrow: 'En cours',
    title: 'Rédaction en cours',
    text: "L'architecte rédige les documents de votre dossier.",
    ctaLabel: 'Voir les documents',
  },
  [ProjectStep.PROJECT_FILE_COMPLETED]: {
    tone: 'blue',
    eyebrow: 'Bientôt',
    title: 'Documents prêts',
    text: 'Les documents seront bientôt envoyés pour signature.',
    ctaLabel: 'Voir les documents',
  },
  [ProjectStep.PROJECT_FILE_SENT]: {
    tone: 'amber',
    eyebrow: 'À vous de jouer',
    title: 'Faites signer les documents',
    text: 'Téléchargez, faites signer et déposez les documents auprès de la mairie.',
    ctaLabel: 'Voir les documents',
  },
  [ProjectStep.PROJECT_FILE_SUBMITTED]: {
    tone: 'blue',
    eyebrow: 'En cours',
    title: 'Dossier en mairie',
    text: 'Votre dossier est en cours d’instruction. Patience.',
    // No CTA while the town hall instructs the dossier — the salesman has
    // nothing actionable. The InstructionTimerNotice (rendered by ProjectHub
    // at this step) shows the instruction countdown instead. Empty ctaLabel
    // suppresses the button (see `hasCta` in ActionBanner.tsx).
    ctaLabel: '',
  },
  [ProjectStep.PROJECT_FILE_REFUSED]: {
    tone: 'amber',
    eyebrow: 'À corriger',
    title: 'Documents à reprendre',
    text: "L'architecte adapte le dossier suite au retour de la mairie.",
    ctaLabel: 'Voir le suivi',
  },
  [ProjectStep.PROJECT_COMPLETED]: {
    tone: 'success',
    eyebrow: 'Terminé',
    title: 'Projet terminé',
    text: 'Votre dossier a été accepté en mairie. Bravo !',
    ctaLabel: 'Voir le projet',
  },
  [ProjectStep.PROJECT_ABANDONED]: {
    tone: 'danger',
    eyebrow: 'Clôturé',
    title: 'Projet abandonné',
    text: "Le projet a été abandonné. Contactez votre architecte pour plus d'informations.",
    // No CTA — the dossier is closed. "Voir le projet" pointed at the Documents
    // page of the very project already on screen, so it read as an action on a
    // dossier that has none left. The section-head buttons (Informations /
    // Activité / Documents) still reach everything. Empty ctaLabel suppresses
    // the button (see `hasCta` in ActionBanner.tsx).
    ctaLabel: '',
  },
  [ProjectStep.PROJECT_REFUSED]: {
    // PROJECT_REFUSED is NOT a terminal state in this codebase. Per
    // #326 D6 (Clarifications session 2026-05-11), its operational
    // meaning is "Le dossier est en attente de modification par le
    // client" — the client/salesman must rework the dossier before
    // the architect can re-process it. The salesman inline editor is
    // editable at this step (see isEditableStep below + FR-010b).
    // Same backend transition as PROJECT_INITIALIZED → PROJECT_REQUESTED.
    tone: 'amber',
    eyebrow: 'À vous de jouer',
    title: 'Reprenez la demande pour la transmettre à nouveau',
    text: 'Il reste à corriger : {remainingItems}.',
    ctaLabel: 'Renvoyer la demande',
    ctaAction: 'request',
    gated: true,
  },
} as Record<ProjectStepType, ActionBannerCopy>

// At PROJECT_FILE_SENT the instruction depends entirely on WHO lodges the
// dossier, and the two are opposites. The table above carries the CLIENT
// wording — what the product always assumed, and what every dossier sent before
// `projects.deposit_mode` existed was sent under, so an unset mode keeps it.
// This is the override for the dossiers the architect files online.
const ARCHITECT_DEPOSIT_BANNER: ActionBannerCopy = {
  tone: 'blue',
  eyebrow: 'En cours',
  title: 'Nous déposons le dossier',
  text:
    'L’architecte dépose le dossier en ligne auprès de la mairie. ' +
    'Rien à faire de votre côté.',
  ctaLabel: 'Voir les documents',
}

export const actionBannerForStep = (
  step: ProjectStepType,
  depositMode?: string | null,
): ActionBannerCopy =>
  step === ProjectStep.PROJECT_FILE_SENT && depositedByArchitect(depositMode)
    ? ARCHITECT_DEPOSIT_BANNER
    : ACTION_BANNERS[step]

/**
 * The lifecycle stepper's one-line explanation of the "Envoi" phase, which has
 * the same problem as the banner: "puis déposés en mairie" describes the
 * commercial's errand, not ours.
 */
export const sendingStepExplanation = (depositMode?: string | null): string =>
  depositedByArchitect(depositMode)
    ? 'Les documents sont finalisés, puis déposés en ligne par l’architecte.'
    : 'Les documents vous sont transmis pour signature, puis déposés en mairie.'

// Per #326 spec FR-010b + D6 (Clarifications session 2026-05-11): the salesman
// inline editor is editable only during the two "client must work the dossier"
// steps — the initial pre-submit phase (PROJECT_INITIALIZED) and the rework
// phase when the dossier comes back to the client (PROJECT_REFUSED, NOT a
// terminal state in this codebase despite the legacy "Clôturé" copy — its
// operational meaning is "Le dossier est en attente de modification par le
// client"). Every other step is read-only for the salesman.
//
// The send-gate CTA disabled-state derives from the pill counts regardless of
// editability — a non-editable step simply means the editor doesn't render,
// not that the gate disappears.
const SALESMAN_EDITABLE_STEPS: ReadonlySet<ProjectStepType> =
  new Set<ProjectStepType>([
    ProjectStep.PROJECT_INITIALIZED,
    ProjectStep.PROJECT_REFUSED,
  ])

export const isEditableStep = (step: ProjectStepType): boolean =>
  SALESMAN_EDITABLE_STEPS.has(step)
